import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Checkbox, ConfirmDialog, EmptyState, ErrorState, Field, Input, Modal, Select, StatusPill, Switch } from '@bb/ui';
import {
  ApiError,
  type ApnsEnvironment,
  type AppInstanceSummary,
  type PushCredentialBody,
  type PushCredentialCheckView,
  type PushCredentialView,
  type PushTestSendBody,
} from '../../../api/client.ts';
import { formatUtc, problemFieldErrors, problemText } from './problems.ts';
import { pushKeys, useApi } from './usePushSettingsData.ts';

const APNS_ENVIRONMENTS: ApnsEnvironment[] = ['sandbox', 'production'];
const PROVIDER_LABEL = { fcm: 'FCM (Firebase Cloud Messaging)', apns: 'APNs (Apple Push Notification service)' } as const;
const HEALTH: Record<string, { tone: 'success' | 'warning' | 'danger' | 'neutral'; label: string }> = {
  healthy: { tone: 'success', label: 'Healthy' },
  unverified: { tone: 'warning', label: 'Unverified' },
  failing: { tone: 'danger', label: 'Failing' },
};
/** A secret reference names where the server reads the secret; anything else is most likely the secret pasted by mistake. */
const SECRET_REF = /^(env:[A-Za-z_][A-Za-z0-9_]*|file:\/\S+)$/;
const APPLE_ID = /^[A-Z0-9]{10}$/;

const isNotFound = (error: unknown) => error instanceof ApiError && error.status === 404;

/**
 * Push provider credentials of the environment's Android and iOS apps (BB-72): which secret reference each uses, its
 * health and last check, the kill switch, a health check, a replacement and a test push. Only metadata is ever shown —
 * the secret stays where `secretRef` points on the server. Reading needs `integrations.manage` like every change.
 */
export function CredentialsSection({ environmentId, apps, canManage, canTestSend }: { environmentId: string; apps: AppInstanceSummary[]; canManage: boolean; canTestSend: boolean }) {
  if (!canManage) return <p className="hint">Credentials and their health need the integrations.manage permission.</p>;
  if (apps.length === 0) return <EmptyState title="No Android or iOS apps" description="Add an app under Apps to give it a push credential." />;
  return (
    <div className="push-credentials">
      {apps.map((app) => (
        <CredentialCard key={app.id} environmentId={environmentId} app={app} canTestSend={canTestSend} />
      ))}
    </div>
  );
}

function CredentialCard({ environmentId, app, canTestSend }: { environmentId: string; app: AppInstanceSummary; canTestSend: boolean }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const key = pushKeys.credential(environmentId, app.id);
  const credential = useQuery({ queryKey: key, queryFn: () => api.pushCredential(environmentId, app.id) });
  const [editing, setEditing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const store = (next: PushCredentialView) => queryClient.setQueryData(key, next);
  const sending = useMutation({
    mutationFn: (sendingEnabled: boolean) => api.setPushCredentialSending(environmentId, app.id, sendingEnabled),
    onSuccess: (next) => {
      store(next);
      setStopping(false);
    },
  });
  const check = useMutation({ mutationFn: () => api.checkPushCredential(environmentId, app.id), onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }) });
  const headingId = `push-credential-${app.id}`;
  const missing = credential.isError && isNotFound(credential.error);
  const c = credential.data;

  return (
    <article className="push-credential" aria-labelledby={headingId}>
      <h3 id={headingId}>{app.name}</h3>
      <p className="hint">
        {app.platform === 'ios' ? 'iOS' : 'Android'} · <code>{app.identifier}</code>
      </p>
      {credential.isPending && <p aria-busy="true">Loading credential…</p>}
      {credential.isError && !missing && <ErrorState title="The credential could not be loaded" message={problemText(credential.error)} onRetry={() => void credential.refetch()} />}
      {missing && (
        <>
          <p className="notice notice--warning">No credential yet: this app cannot get push.</p>
          <Button variant="primary" onClick={() => setEditing(true)}>
            Add credential
          </Button>
        </>
      )}
      {c && (
        <>
          <dl className="details">
            <dt>Provider</dt>
            <dd>{PROVIDER_LABEL[c.provider]}</dd>
            <dt>Secret reference</dt>
            <dd>
              <code>{c.secretRef}</code>
            </dd>
            {c.provider === 'fcm' ? (
              <>
                <dt>Firebase project</dt>
                <dd>{c.fcmProjectId ?? 'From the service account'}</dd>
              </>
            ) : (
              <>
                <dt>Team ID</dt>
                <dd>{c.apnsTeamId}</dd>
                <dt>Key ID</dt>
                <dd>{c.apnsKeyId}</dd>
                <dt>Topic</dt>
                <dd>{c.apnsTopic ?? `${app.identifier} (the bundle ID)`}</dd>
                <dt>Environments</dt>
                <dd>{c.apnsEnvironments.join(', ')}</dd>
              </>
            )}
            <dt>Health</dt>
            <dd>
              <StatusPill tone={HEALTH[c.healthStatus]?.tone ?? 'neutral'}>{HEALTH[c.healthStatus]?.label ?? c.healthStatus}</StatusPill>
            </dd>
            <dt>Last check</dt>
            <dd>{formatUtc(c.lastCheckedAt)}</dd>
            <dt>Last success</dt>
            <dd>{formatUtc(c.lastSuccessAt)}</dd>
            <dt>Last error</dt>
            <dd>{c.lastErrorCode ? `${c.lastErrorCode} (${c.consecutiveCredentialErrors} in a row)` : 'None'}</dd>
          </dl>
          <p className="hint">The secret stays on the server where the reference points; it is never shown or sent here. Replace the credential after rotating the key.</p>
          <Switch label="Sending enabled" checked={c.sendingEnabled} disabled={sending.isPending} onCheckedChange={(on) => (on ? sending.mutate(true) : setStopping(true))} />
          {!c.sendingEnabled && <p className="notice notice--warning">Sending is off: pushes of this app are cancelled until it is switched back on.</p>}
          {sending.isError && <p role="alert">{problemText(sending.error)}</p>}
          <div className="actions">
            <Button loading={check.isPending} onClick={() => check.mutate()}>
              Check now
            </Button>
            <Button onClick={() => setEditing(true)}>Replace credential</Button>
            {canTestSend && (
              <Button onClick={() => setTesting(true)} disabled={!c.sendingEnabled}>
                Send test push
              </Button>
            )}
          </div>
          {check.data && <CheckResult result={check.data} />}
          {check.isError && <p role="alert">{problemText(check.error)}</p>}
          <ConfirmDialog
            open={stopping}
            onOpenChange={(open) => !open && setStopping(false)}
            title={`Stop push for ${app.name}?`}
            confirmLabel="Stop sending"
            busy={sending.isPending}
            onConfirm={() => sending.mutate(false)}
          >
            Queued pushes of this app are cancelled and nothing is sent until sending is switched back on. Health checks still work.
          </ConfirmDialog>
        </>
      )}
      {editing && <CredentialDialog environmentId={environmentId} app={app} current={c ?? null} onClose={() => setEditing(false)} onSaved={store} />}
      {testing && c && <TestPushDialog environmentId={environmentId} app={app} credential={c} onClose={() => setTesting(false)} />}
    </article>
  );
}

function CheckResult({ result }: { result: PushCredentialCheckView }) {
  const latency = result.latencyMs !== null ? ` (${result.latencyMs} ms)` : '';
  const text =
    result.verdict === 'healthy'
      ? `The provider accepted the credential${latency}.`
      : result.verdict === 'credential_error'
        ? `The provider refused the credential: ${result.code}${result.providerCode ? ` / ${result.providerCode}` : ''}${result.detail ? ` — ${result.detail}` : ''}.`
        : `Inconclusive: ${result.code}${result.detail ? ` — ${result.detail}` : ''}. Health did not change.`;
  return (
    <p role="status" className={result.verdict === 'healthy' ? 'hint' : 'notice notice--warning'}>
      {text}
    </p>
  );
}

interface CredentialDraft {
  secretRef: string;
  fcmProjectId: string;
  apnsTeamId: string;
  apnsKeyId: string;
  apnsTopic: string;
  apnsEnvironments: ApnsEnvironment[];
}

const draftOf = (c: PushCredentialView | null): CredentialDraft => ({
  secretRef: c?.secretRef ?? '',
  fcmProjectId: c?.fcmProjectId ?? '',
  apnsTeamId: c?.apnsTeamId ?? '',
  apnsKeyId: c?.apnsKeyId ?? '',
  apnsTopic: c?.apnsTopic ?? '',
  apnsEnvironments: c?.apnsEnvironments.length ? APNS_ENVIRONMENTS.filter((e) => c.apnsEnvironments.includes(e)) : ['production'],
});

/** The body to send, or one message per field. */
function checkCredential(provider: 'fcm' | 'apns', d: CredentialDraft): { body: PushCredentialBody | null; issues: Record<string, string> } {
  const issues: Record<string, string> = {};
  const secretRef = d.secretRef.trim();
  if (!SECRET_REF.test(secretRef)) issues.secretRef = 'Enter a reference such as env:FCM_SA_ANDROID or file:/run/secrets/key.json, not the secret itself.';
  if (provider === 'fcm') {
    if (d.fcmProjectId.trim().length > 128) issues.fcmProjectId = 'At most 128 characters.';
    if (Object.keys(issues).length) return { body: null, issues };
    return { body: { provider, secretRef, ...(d.fcmProjectId.trim() ? { fcmProjectId: d.fcmProjectId.trim() } : {}) }, issues };
  }
  if (!APPLE_ID.test(d.apnsTeamId.trim())) issues.apnsTeamId = '10 capital letters or digits, from Membership details.';
  if (!APPLE_ID.test(d.apnsKeyId.trim())) issues.apnsKeyId = '10 capital letters or digits, from the key page.';
  if (d.apnsTopic.trim().length > 255) issues.apnsTopic = 'At most 255 characters.';
  if (d.apnsEnvironments.length === 0) issues.apnsEnvironments = 'Choose at least one environment.';
  if (Object.keys(issues).length) return { body: null, issues };
  return {
    body: { provider, secretRef, apnsTeamId: d.apnsTeamId.trim(), apnsKeyId: d.apnsKeyId.trim(), ...(d.apnsTopic.trim() ? { apnsTopic: d.apnsTopic.trim() } : {}), apnsEnvironments: d.apnsEnvironments },
    issues,
  };
}

/** Server problems on fields: 400 issues by path, 422 `credential_invalid` on the reference. */
function serverIssues(error: unknown): { fields: Record<string, string>; form: string | null } {
  if (!error) return { fields: {}, form: null };
  const fields = problemFieldErrors(error);
  if (error instanceof ApiError && error.code === 'credential_invalid') fields.secretRef ??= error.message;
  return { fields, form: Object.keys(fields).length ? null : problemText(error) };
}

function CredentialDialog({ environmentId, app, current, onClose, onSaved }: { environmentId: string; app: AppInstanceSummary; current: PushCredentialView | null; onClose: () => void; onSaved: (c: PushCredentialView) => void }) {
  const api = useApi();
  const provider = app.platform === 'ios' ? 'apns' : 'fcm';
  const [draft, setDraft] = useState(() => draftOf(current));
  const save = useMutation({
    mutationFn: (body: PushCredentialBody) => api.putPushCredential(environmentId, app.id, body),
    onSuccess: (saved) => {
      onSaved(saved);
      onClose();
    },
  });
  const checked = checkCredential(provider, draft);
  const server = serverIssues(save.error);
  const issue = (key: keyof CredentialDraft) => checked.issues[key] ?? server.fields[key];
  const set = (patch: Partial<CredentialDraft>) => {
    save.reset();
    setDraft((d) => ({ ...d, ...patch }));
  };
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={`${current ? 'Replace' : 'Add'} credential — ${app.name}`}
      description={provider === 'fcm' ? 'An FCM HTTP v1 service account of the Firebase project.' : 'A token-based APNs key (.p8) of the Apple team.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!checked.body} onClick={() => checked.body && save.mutate(checked.body)}>
            Save credential
          </Button>
        </>
      }
    >
      <div className="stack-form">
        <Field
          label="Secret reference"
          hint={`Where the api and worker read the ${provider === 'fcm' ? 'service account JSON' : '.p8 key'}: env:NAME (a variable) or file:/path (inside their secrets directory). Never paste the key itself.`}
          error={issue('secretRef')}
        >
          <Input value={draft.secretRef} spellCheck={false} autoComplete="off" placeholder={provider === 'fcm' ? 'env:FCM_SA_ANDROID' : 'env:APNS_P8_IOS'} onChange={(e) => set({ secretRef: e.target.value })} />
        </Field>
        {provider === 'fcm' ? (
          <Field label="Firebase project ID (optional)" hint="Must match project_id of the service account; taken from it when empty." error={issue('fcmProjectId')}>
            <Input value={draft.fcmProjectId} spellCheck={false} onChange={(e) => set({ fcmProjectId: e.target.value })} />
          </Field>
        ) : (
          <>
            <Field label="Team ID" error={issue('apnsTeamId')}>
              <Input value={draft.apnsTeamId} spellCheck={false} onChange={(e) => set({ apnsTeamId: e.target.value })} />
            </Field>
            <Field label="Key ID" error={issue('apnsKeyId')}>
              <Input value={draft.apnsKeyId} spellCheck={false} onChange={(e) => set({ apnsKeyId: e.target.value })} />
            </Field>
            <Field label="Topic (bundle ID)" hint="Empty — the app identifier." error={issue('apnsTopic')}>
              <Input value={draft.apnsTopic} spellCheck={false} placeholder={app.identifier} onChange={(e) => set({ apnsTopic: e.target.value })} />
            </Field>
            <fieldset>
              <legend>APNs environments</legend>
              {APNS_ENVIRONMENTS.map((env) => (
                <Checkbox
                  key={env}
                  label={env === 'sandbox' ? 'Sandbox' : 'Production'}
                  checked={draft.apnsEnvironments.includes(env)}
                  onCheckedChange={(on) => set({ apnsEnvironments: APNS_ENVIRONMENTS.filter((e) => (e === env ? on : draft.apnsEnvironments.includes(e))) })}
                />
              ))}
              <p className="hint">Sandbox — debug builds; production — TestFlight and the App Store. Health checks use the first one.</p>
              {issue('apnsEnvironments') && (
                <p className="bb-field__error" role="alert">
                  {issue('apnsEnvironments')}
                </p>
              )}
            </fieldset>
          </>
        )}
        <p className="hint">Saving checks that the secret resolves and parses, and resets health to unverified.</p>
        {server.form && <p role="alert">{server.form}</p>}
      </div>
    </Modal>
  );
}

function TestPushDialog({ environmentId, app, credential, onClose }: { environmentId: string; app: AppInstanceSummary; credential: PushCredentialView; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const environments = APNS_ENVIRONMENTS.filter((e) => credential.apnsEnvironments.includes(e));
  const [draft, setDraft] = useState({ token: '', title: '', body: '', apnsEnvironment: (environments[0] ?? 'production') as ApnsEnvironment });
  const send = useMutation({
    mutationFn: (body: PushTestSendBody) => api.testSendPushCredential(environmentId, app.id, body),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: pushKeys.credential(environmentId, app.id) }),
  });
  const ready = draft.token.trim() && draft.title.trim() && draft.body.trim() && draft.title.length <= 256 && draft.body.length <= 1024;
  const body: PushTestSendBody = { token: draft.token.trim(), title: draft.title.trim(), body: draft.body.trim(), ...(credential.provider === 'apns' ? { apnsEnvironment: draft.apnsEnvironment } : {}) };
  const set = (patch: Partial<typeof draft>) => {
    send.reset();
    setDraft((d) => ({ ...d, ...patch }));
  };
  const result = send.data;
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Test push — ${app.name}`}
      description="One visible push to one device, right now. It is not stored with the token and never reaches campaign reports."
      footer={
        <Button variant="primary" loading={send.isPending} disabled={!ready} onClick={() => send.mutate(body)}>
          Send
        </Button>
      }
    >
      <div className="stack-form">
        <Field label="Device token" hint="From the SDK debugger or the app's logs.">
          <Input value={draft.token} spellCheck={false} autoComplete="off" onChange={(e) => set({ token: e.target.value })} />
        </Field>
        <Field label="Title">
          <Input value={draft.title} maxLength={256} onChange={(e) => set({ title: e.target.value })} />
        </Field>
        <Field label="Message">
          <Input value={draft.body} maxLength={1024} onChange={(e) => set({ body: e.target.value })} />
        </Field>
        {credential.provider === 'apns' && (
          <Field label="APNs environment" hint="Where the token comes from: a debug build uses sandbox.">
            <Select options={environments.map((e) => ({ value: e, label: e }))} value={draft.apnsEnvironment} onChange={(e) => set({ apnsEnvironment: e.target.value as ApnsEnvironment })} />
          </Field>
        )}
        {result && (
          <p role="status" className={result.status === 'accepted' ? 'hint' : 'notice notice--warning'}>
            {result.status === 'accepted'
              ? `Accepted by the provider (HTTP ${result.httpStatus ?? '—'}, ${result.latencyMs ?? '—'} ms).`
              : `${result.status === 'failed' ? 'Failed' : 'Unknown outcome'}: ${result.code}${result.providerCode ? ` / ${result.providerCode}` : ''}${result.httpStatus ? ` (HTTP ${result.httpStatus})` : ''}.`}
          </p>
        )}
        {send.isError && <p role="alert">{problemText(send.error)}</p>}
      </div>
    </Modal>
  );
}
